const textAt = (view, offset, length) => {
  let value = '';
  for (let index = 0; index < length; index += 1) {
    value += String.fromCharCode(view.getUint8(offset + index));
  }
  return value;
};

const readInt24 = (view, offset, littleEndian) => {
  const a = view.getUint8(offset + (littleEndian ? 0 : 2));
  const b = view.getUint8(offset + 1);
  const c = view.getUint8(offset + (littleEndian ? 2 : 0));
  const value = a | (b << 8) | (c << 16);
  return value & 0x800000 ? value - 0x1000000 : value;
};

/**
 * Decode uncompressed PCM/float WAV data without relying on the browser codec
 * registry. Some drum kits contain perfectly valid WAV variants that
 * decodeAudioData rejects even though their sample data is straightforward.
 */
export function decodePcmWav(audioContext, bytes) {
  const view = new DataView(bytes);
  if (view.byteLength < 44) throw new Error('The WAV file is incomplete.');
  const container = textAt(view, 0, 4);
  const littleEndian = container === 'RIFF';
  if (!littleEndian && container !== 'RIFX') throw new Error('This is not a WAV file.');
  if (textAt(view, 8, 4) !== 'WAVE') throw new Error('This is not a WAV file.');

  let format = null;
  let dataOffset = -1;
  let dataSize = 0;
  let offset = 12;
  while (offset + 8 <= view.byteLength) {
    const chunkId = textAt(view, offset, 4);
    const declaredSize = view.getUint32(offset + 4, littleEndian);
    const chunkOffset = offset + 8;
    const chunkSize = Math.min(declaredSize, Math.max(0, view.byteLength - chunkOffset));
    if (chunkId === 'fmt ' && chunkSize >= 16) {
      let formatTag = view.getUint16(chunkOffset, littleEndian);
      if (formatTag === 0xfffe && chunkSize >= 40) {
        formatTag = view.getUint16(chunkOffset + 24, littleEndian);
      }
      format = {
        formatTag,
        channels: view.getUint16(chunkOffset + 2, littleEndian),
        sampleRate: view.getUint32(chunkOffset + 4, littleEndian),
        blockAlign: view.getUint16(chunkOffset + 12, littleEndian),
        bitsPerSample: view.getUint16(chunkOffset + 14, littleEndian)
      };
    } else if (chunkId === 'data') {
      dataOffset = chunkOffset;
      dataSize = chunkSize;
    }
    offset = chunkOffset + declaredSize + (declaredSize % 2);
  }

  if (!format || dataOffset < 0) throw new Error('The WAV file has no playable audio data.');
  const { formatTag, channels, sampleRate, blockAlign, bitsPerSample } = format;
  if (![1, 3].includes(formatTag)) throw new Error('This WAV uses a compressed codec that the browser cannot decode.');
  if (!channels || !sampleRate || !blockAlign) throw new Error('The WAV format information is invalid.');
  if ((formatTag === 1 && ![8, 16, 24, 32].includes(bitsPerSample))
      || (formatTag === 3 && ![32, 64].includes(bitsPerSample))) {
    throw new Error(`This ${bitsPerSample}-bit WAV format is not supported.`);
  }

  const frameCount = Math.floor(dataSize / blockAlign);
  if (!frameCount) throw new Error('The WAV file contains no audio frames.');
  const buffer = audioContext.createBuffer(channels, frameCount, sampleRate);
  const bytesPerSample = bitsPerSample / 8;
  for (let channel = 0; channel < channels; channel += 1) {
    const output = buffer.getChannelData(channel);
    for (let frame = 0; frame < frameCount; frame += 1) {
      const sampleOffset = dataOffset + (frame * blockAlign) + (channel * bytesPerSample);
      let sample;
      if (formatTag === 3) {
        sample = bitsPerSample === 32
          ? view.getFloat32(sampleOffset, littleEndian)
          : view.getFloat64(sampleOffset, littleEndian);
      } else if (bitsPerSample === 8) {
        sample = (view.getUint8(sampleOffset) - 128) / 128;
      } else if (bitsPerSample === 16) {
        sample = view.getInt16(sampleOffset, littleEndian) / 32768;
      } else if (bitsPerSample === 24) {
        sample = readInt24(view, sampleOffset, littleEndian) / 8388608;
      } else {
        sample = view.getInt32(sampleOffset, littleEndian) / 2147483648;
      }
      output[frame] = Number.isFinite(sample) ? Math.max(-1, Math.min(1, sample)) : 0;
    }
  }
  return buffer;
}

export async function decodeAudioFile(audioContext, bytes, fileName = '') {
  try {
    return await audioContext.decodeAudioData(bytes.slice(0));
  } catch (browserError) {
    const view = new DataView(bytes);
    const isWav = /\.wav(?:e)?$/i.test(String(fileName).split('?')[0])
      || (view.byteLength >= 12 && ['RIFF', 'RIFX'].includes(textAt(view, 0, 4)) && textAt(view, 8, 4) === 'WAVE');
    if (!isWav) throw browserError;
    return decodePcmWav(audioContext, bytes);
  }
}

export function isAudioFile(file = {}) {
  const name = String(file.name || file.filename || '').split('?')[0].toLowerCase();
  return String(file.type || '').toLowerCase().startsWith('audio/')
    || ['.wav', '.wave', '.mp3', '.ogg', '.opus', '.m4a', '.aac', '.flac', '.aif', '.aiff']
      .some((extension) => name.endsWith(extension));
}
